import { Box, ListItemText, Checkbox } from '@mui/material'
import { FormControl, IconButton, InputAdornment, InputLabel, MenuItem, Select, TextField } from '@mui/material_modern'
import { noop } from 'lodash'
import React, { useState } from 'react'
import SearchIcon from '@mui/icons-material_modern/Search'
import { getISOLocaleMidnightTime } from 'utils/data-manipulation/valueFormatter.jsx'
import { DATE_RANGE_OPTIONS, DISPATCH_TICKET_STATUSES, DISPATCH_TICKET_EXCLUDE_LINKED_FILTER } from 'utils/constants'
import { addToDate } from 'utils/data-manipulation/common'

type filter = {
  search: string
  scheduleDateFrom: string
  scheduleDateTo: string
  driver: string
  customer: string
  customerId: string
  statuses: string[]
  types: string[]
  excludeLinkedTickets: DISPATCH_TICKET_EXCLUDE_LINKED_FILTER
  excludeIntercompanyFromTypeFilter: boolean
  endYardLocationId: string | undefined
}

interface DispatchTicketFilterBarProps {
  filters: filter
  setFilter: (filter: filter) => void
  inbound?: boolean
}

const filterBarStyles = {
  root: {
    display: 'flex',
    alignItems: 'center',
    p: 2,
    gap: 2,
  },
}

export const DispatchTicketFilterBar: React.FC<DispatchTicketFilterBarProps> = ({
  filters = {
    search: '',
    scheduleDateFrom: '',
    scheduleDateTo: '',
    driver: '',
    customer: '',
    customerId: '',
    statuses: [],
  },
  setFilter = noop,
  inbound = false,
}) => {
  const [dateRange, setDateRange] = useState(DATE_RANGE_OPTIONS.PAST_7_DAYS)
  const [statuses, setStatuses] = useState<string[]>(filters.statuses)

  const ticketStatuses = Object.values(DISPATCH_TICKET_STATUSES).filter(status => status.id !== 'VOID')
  const isAllSelected = statuses.length === ticketStatuses.length

  const handleFilterChange = filterName => event => {
    const { value } = event.target

    if (filterName === 'statuses') {
      const selectedStatuses =
        value[value.length - 1] === 'ALL' ? (isAllSelected ? [] : ticketStatuses.map(status => status.id)) : value

      const filteredStatuses = selectedStatuses.filter(status => status !== 'ALL')
      setStatuses(filteredStatuses)
      setFilter(prevFilters => ({
        ...prevFilters,
        [filterName]: filteredStatuses.length > 0 ? filteredStatuses : ticketStatuses.map(status => status.id),
      }))
    } else {
      setFilter(prevFilters => ({ ...prevFilters, [filterName]: value }))
    }
  }

  const handleDateRangeChange = event => {
    const { value } = event.target
    setDateRange(value)
    const today = new Date()
    const todayISO = today.toISOString()
    const endOfTodayISO = getISOLocaleMidnightTime(addToDate(todayISO, 1))

    switch (value) {
      case DATE_RANGE_OPTIONS.TODAY:
        setFilter(prevFilters => ({
          ...prevFilters,
          scheduleDateFrom: getISOLocaleMidnightTime(today),
          scheduleDateTo: endOfTodayISO,
        }))
        break
      case DATE_RANGE_OPTIONS.PAST_7_DAYS:
        setFilter(prevFilters => ({
          ...prevFilters,
          scheduleDateFrom: getISOLocaleMidnightTime(addToDate(todayISO, -7)),
          scheduleDateTo: endOfTodayISO,
        }))
        break
      case DATE_RANGE_OPTIONS.PAST_30_DAYS:
        setFilter(prevFilters => ({
          ...prevFilters,
          scheduleDateFrom: getISOLocaleMidnightTime(addToDate(todayISO, -30)),
          scheduleDateTo: endOfTodayISO,
        }))
        break
      default:
        setFilter(prevFilters => ({
          ...prevFilters,
          scheduleDateFrom: '',
          scheduleDateTo: '',
        }))
        break
    }
  }

  return (
    <Box sx={filterBarStyles.root}>
      <TextField
        value={filters.search}
        onChange={e => handleFilterChange('search')(e)}
        placeholder="Search for ticket"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <IconButton>
                <SearchIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          flexGrow: 1,
          maxWidth: 300,
        }}
      />

      <FormControl sx={{ minWidth: 150 }}>
        <InputLabel>Date Range</InputLabel>
        <Select label="Date Range" value={dateRange} onChange={handleDateRangeChange}>
          {Object.keys(DATE_RANGE_OPTIONS).map(enumKey => (
            <MenuItem key={enumKey} value={DATE_RANGE_OPTIONS[enumKey]}>
              {DATE_RANGE_OPTIONS[enumKey]}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {inbound && (
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={statuses}
            onChange={e => handleFilterChange('statuses')(e)}
            multiple
            renderValue={selected =>
              selected.length === 1
                ? ticketStatuses.find(status => status.id === selected[0])?.name
                : `${selected.length} Selected`
            }
          >
            <MenuItem value="ALL">
              <Checkbox
                checked={isAllSelected}
                indeterminate={statuses.length > 0 && statuses.length < ticketStatuses.length}
              />
              <ListItemText primary="Select All" />
            </MenuItem>
            {ticketStatuses.map(status => (
              <MenuItem key={status.name} value={status.id}>
                <Checkbox checked={statuses.includes(status.id)} />
                {status.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/*! DISABLING FOR NOW WHILE DRIVERS WORK IS IN PROGRESS */}

      {/* <FormControl sx={{ minWidth: 120 }}> */}
      {/*   <InputLabel>Drivers</InputLabel> */}
      {/*   <Select */}
      {/*     value={filters.driver} */}
      {/*     onChange={e => handleFilterChange('driver')(e)} */}
      {/*     label="Drivers" */}
      {/*   > */}
      {/*     {dummyDrivers.map(option => ( */}
      {/*       <MenuItem key={option} value={option}>{option}</MenuItem> */}
      {/*     ))} */}
      {/*   </Select> */}
      {/* </FormControl> */}
    </Box>
  )
}
